import { useState } from 'react';
import { Loader2, Languages } from 'lucide-react';
import { aiTranslate } from '@/lib/aiApi';

const LANGUAGES = [
  'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Dutch', 'Polish',
  'Russian', 'Japanese', 'Korean', 'Chinese (Simplified)', 'Chinese (Traditional)',
  'Arabic', 'Hindi', 'Turkish', 'Vietnamese', 'Thai', 'Indonesian',
];

export default function TranslateTab({ file, documentHash }) {
  const [targetLanguage, setTarget] = useState('Spanish');
  const [mode, setMode] = useState('summary');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const data = await aiTranslate(documentHash
        ? { documentHash, targetLanguage, mode }
        : { file, targetLanguage, mode });
      setResult(data);
    } catch (err) {
      setError(err.message || 'Translation failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-4 overflow-y-auto h-full">
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="text-gray-700 font-medium">Target language</span>
          <select
            value={targetLanguage}
            onChange={(e) => setTarget(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          >
            {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </label>
        <fieldset className="flex gap-4 text-sm">
          <label className="flex items-center gap-1">
            <input type="radio" value="summary" checked={mode === 'summary'} onChange={() => setMode('summary')} />
            Summary
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" value="document" checked={mode === 'document'} onChange={() => setMode('document')} />
            Full document
          </label>
        </fieldset>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          className="w-full rounded-lg bg-indigo-600 text-white py-2 text-sm font-semibold hover:bg-indigo-700 disabled:bg-indigo-300 inline-flex items-center justify-center gap-1"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Translating…</> : <><Languages className="w-4 h-4" /> Translate</>}
        </button>
        {error && <div className="text-sm text-red-600">{error}</div>}
        {result && (
          <div className="mt-2">
            <div className="text-xs text-gray-500 mb-1">{result.targetLanguage} · {result.mode}</div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm whitespace-pre-wrap max-h-[50vh] overflow-auto">
              {result.translatedText}
            </div>
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(result.translatedText)}
              className="mt-2 text-xs text-indigo-600 hover:underline"
            >
              Copy to clipboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
